/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useServiceContext } from '../../root'
import { useTranslation } from 'react-i18next'
import { Select, OptionType } from '../Select/Select'
import Modal from '../Modal'
import { LoadingModal } from './LoadingModal'
import type { DetailedWorkflow } from '../../services/api/workflows'

interface WorkflowCollaboratorsModalProps {
  workflowId: number
  workflow?: any
  onSuccess?: () => void
}

export function WorkflowCollaboratorsModal({
  workflowId,
  workflow: workflowProp,
  onSuccess
}: WorkflowCollaboratorsModalProps) {
  const context = useServiceContext()
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const [workflow, setWorkflow] = useState<DetailedWorkflow | null>(workflowProp || null)
  const [collaborators, setCollaborators] = useState<string[]>([])
  const [usersGroupsOptions, setUsersGroupsOptions] = useState<OptionType[]>([])
  const [loading, setLoading] = useState(!workflowProp)
  const [saving, setSaving] = useState(false)
  const [isOpen, setIsOpen] = useState(true)

  useEffect(() => {
    if (!workflowProp) {
      loadWorkflow()
    }
    loadUsersGroups()
  }, [])

  const loadWorkflow = async () => {
    try {
      const data = await context.api.workflows.getWorkflow(workflowId)
      setWorkflow(data)
    } catch (error) {
      console.error('Failed to load workflow:', error)
      context.service.toast.error(t('ERROR_LOADING_WORKFLOW') || 'Failed to load workflow')
      handleClose()
    } finally {
      setLoading(false)
    }
  }

  const loadUsersGroups = async () => {
    try {
      const usersGroupsData = await context.api.users.getAssembledUsersGroups(false)
      const options = usersGroupsData.merged.map((item: any) => ({
        value: `${item.systemHelperType}-${item.id}`,
        label: item.name
      }))
      setUsersGroupsOptions(options)
    } catch (error) {
      console.error('Failed to load users/groups:', error)
    }
  }

  const handleClose = () => {
    setIsOpen(false)
    if (onSuccess) {
      onSuccess()
    }
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose()
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Transform collaborators array from 'USER-123' format to {id: '123', systemHelperType: 'USER'}
    const transformedCollaborators = collaborators.map((item) => {
      const userSelectionObj = item.split('-')
      return {
        id: userSelectionObj[1],
        systemHelperType: userSelectionObj[0]
      }
    })

    setSaving(true)
    try {
      await context.api.workflows.addCollaborators(workflowId, transformedCollaborators)

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['workflows'] })
      queryClient.invalidateQueries({ queryKey: ['workflow', workflowId] })

      context.service.toast.success(t('CHANGE_REQUEST_EDIT_SUCCESS_MSG') || 'Collaborators added successfully')

      if (onSuccess) {
        onSuccess()
      }

      handleClose()
    } catch (error: any) {
      console.error('Failed to add collaborators:', error)
      const errorMessage = error?.body?.message || error?.message || 'Failed to add collaborators'
      context.service.toast.error(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <LoadingModal
        title={t('CR_ACTION_ADD_COLLABORATORS_TO_CURRENT_PHASE') || 'Add Collaborators to Current Phase'}
        onBackdropClick={handleBackdropClick}
        onClose={handleClose}
      />
    )
  }

  if (!workflow) {
    return null
  }

  return (
    <Modal
      show={isOpen}
      handleClose={handleClose}
      title={t('CR_ACTION_ADD_COLLABORATORS_TO_CURRENT_PHASE') || 'Add Collaborators to Current Phase'}
      size="lg"
    >
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          <div className="form-group">
            <label className="control-label">{t('WORKFLOW_CHANGE_REQUEST_NUMBER')}</label>
            <p className="form-control-static">{workflow.workflowNo}</p>
          </div>

          <div className="form-group">
            <label className="control-label required">{t('USERS_GROUPS')}</label>
            <Select
              id="collaborators-select"
              name="collaborators"
              multi={true}
              options={usersGroupsOptions}
              value={collaborators}
              onChange={(selected) => {
                if (Array.isArray(selected)) {
                  setCollaborators(selected.map((opt) => opt.value))
                } else {
                  setCollaborators([])
                }
              }}
            />
          </div>
        </Modal.Body>

        <Modal.Footer>
          <button type="button" className="btn btn-default" onClick={handleClose} disabled={saving}>
            {t('CANCEL') || 'Cancel'}
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving || collaborators.length === 0}>
            {saving ? (
              <>
                <i className="fa fa-spinner fa-spin" /> {t('SAVING') || 'Saving...'}
              </>
            ) : (
              t('SAVE') || 'Save'
            )}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}

export default WorkflowCollaboratorsModal
